import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { fetchSubProjects, listProjectsByUser, uploadFilesToProject, uploadFileToGCS, } from "@/core/api/api";
import PageNotFound from "@/assets/images/page-not-found.svg";
import useToastStore from "@/shared/zustand/toastStore";
import useProjectStore from "@/shared/zustand/projectManagementStore";
import { ChevronLeft, Check, Folder, X } from "lucide-react";
import { useMainLayoutStore } from "@/shared/zustand/mainLayoutStore";
import { useHostMicroFrontendApi } from "@/shared/microfrontend/hooks/useHostMicroFrontend";
import { MF_EVENTS, } from "@/shared/microfrontend/interfaces/microFrontend";
import { generateUniqueFileName, getFileExtension, } from "@/shared/utils/helpers";
import useDriveStore from "@/shared/zustand/driveModeStore";
import useAuthStore from "@/shared/zustand/authStore";
import { ProjectCard, ProjectCardsSkeleton } from "./ProjectCard";
import { sortOptions } from "@/constants/ProjectDashboardContstants";
import { HOST_MODES } from "@/constants";
const ProjectSelectionDialog = ({ visible, onHide, triggerForPersist, setPersistedData, parentRef, msgDataArtifact, setMsgDataArtifact, setProjectIdFromURL, }) => {
    const mfApi = useHostMicroFrontendApi();
    const { projects, setProjects, selectedProject, setSelectedProject, allProjects, setAllProjects, } = useProjectStore();
    const showToast = useToastStore((state) => state.showToast);
    const { activeMode, currentHostMode, remoteLoadedInfo } = useMainLayoutStore();
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("date-desc");
    const [navigationStack, setNavigationStack] = useState([]);
    const [currentProject, setCurrentProject] = useState(null);
    // const [projects, setProjects] = useState<Project[]>([]);
    const [subprojectsInfo, setSubprojectsInfo] = useState([]);
    const [fetchingProjects, setFetchingProjects] = useState(false);
    const [fetchingSubprojects, setFetchingSubprojects] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [userSelectedProject, setUserSelectedProject] = useState(null);
    const [showUploadDetails, setShowUploadDetails] = useState(false);
    const [currentUploadData, setCurrentUploadData] = useState({ blob: null, projectId: null });
    const [filename, setFilename] = useState("");
    const [fileExtension, setFileExtension] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { selectedFiles, setSelectedFiles, subModeRequest, setSubModeRequest } = useDriveStore();
    const { user } = useAuthStore();
    const [requestLoading, setRequestLoading] = useState(false);
    // console.log("request loading:", requestLoading);
    const handleProjectSelection = (project) => {
        if (project) {
            // If clicking the same project, uncheck it
            setUserSelectedProject((prevSelected) => prevSelected?._id === project._id ? null : project);
        }
    };
    const handleDriveFilesUpload = async (projectId, projectLinks, userSelectedProject) => {
        setRequestLoading(true);
        if (!user || !user.email) {
            console.warn("User details not found");
            showToast("warn", "Warn", "User Details not found");
            setRequestLoading(false);
            return;
        }
        const { responseData, error } = await uploadFilesToProject(projectId, projectLinks, user?.email || "", user?.provider?.access_token || "", user?.provider?.refresh_token || "");
        if (error) {
            // console.log("error", error);
            const errorObj = JSON.stringify(error.errorObjData);
            // console.log(errorObj,"errorObj")
            if (errorObj.includes("You do not have the sufficient permissions to access/download")) {
                showToast("error", "Error", "Could not upload the file, please check if you have sufficient permissions to download it");
            }
            else {
                showToast("error", "Error", error.errorMessage);
            }
            setRequestLoading(false); // reset the loading state
            onHide(); // close the dialog
            return;
        }
        else {
            showToast("success", "Success", "File uploaded successfully");
            console.log("Files uploaded successfully:", responseData);
            setSelectedFiles([]); // to clear the selected files on success
            if (mfApi && subModeRequest && subModeRequest.data) {
                const subModePayload = {
                    ...subModeRequest.data,
                    eventType: "nextMode",
                };
                mfApi.dispatch(MF_EVENTS.TRIGGER_SUBMODE, subModePayload);
                setSubModeRequest(null);
            }
            // converting ProjectResponse to Project
            const projectPayload = {
                _id: userSelectedProject._id,
                name: userSelectedProject.name,
                description: userSelectedProject.description,
                file_urls: userSelectedProject.file_urls,
                user: userSelectedProject.user,
                created_by: userSelectedProject.created_by,
                timestamp: userSelectedProject.timestamp,
                parent: userSelectedProject.parent,
                conversations: userSelectedProject.conversations,
                files: userSelectedProject.files,
            };
            setSelectedProject(projectPayload);
            setProjectIdFromURL(projectPayload._id);
        }
        setRequestLoading(false);
        resetAllStates();
        onHide();
    };
    // Update handleProceed to use resetAllStates
    const handleProceed = async () => {
        if (userSelectedProject) {
            // console.log("user has Selected project:", userSelectedProject);
            if (activeMode && remoteLoadedInfo.modeName === activeMode) {
                // console.log("user has selected:", activeMode, userSelectedProject);
                triggerForPersist(activeMode, userSelectedProject);
                setRequestLoading(true);
            }
            else if (currentHostMode === HOST_MODES["Drive Mode"]) {
                const projectLinks = selectedFiles.map((project) => project.webViewLink);
                await handleDriveFilesUpload(userSelectedProject?._id || "", projectLinks, userSelectedProject);
            }
            //   resetAllStates();
            //   onHide();
        }
    };
    const handleContinue = async () => {
        // console.log("user has a default project:", selectedProject);
        if (selectedProject &&
            activeMode &&
            remoteLoadedInfo.modeName === activeMode) {
            triggerForPersist(activeMode, selectedProject);
            setRequestLoading(true);
        }
        else if (selectedProject &&
            currentHostMode === HOST_MODES["Drive Mode"]) {
            const projectLinks = selectedFiles.map((project) => project.webViewLink);
            await handleDriveFilesUpload(selectedProject?._id || "", projectLinks, selectedProject);
        }
        // resetAllStates();
        // onHide();
    };
    const addToProjectHandler = async (blob, projectId, filename, isAI) => {
        return await uploadFileToGCS(blob, projectId, filename, isAI ?? true);
    };
    const handleBothUploads = async (payload, completeFileName) => {
        const uploadPromises = [];
        uploadPromises.push(addToProjectHandler(payload.data, payload.projectId, completeFileName, true));
        if (msgDataArtifact && msgDataArtifact.artifactData) {
            try {
                const artifactJson = msgDataArtifact.artifactData;
                const artifactBlob = new Blob([JSON.stringify(artifactJson)], {
                    type: "application/json",
                });
                // Create a unique filename for the artifact
                const artifactFileName = `Artifact_${msgDataArtifact.artifactData.artifactTitle}_${msgDataArtifact.artifactData.id}.artifact.json`;
                // Add the second upload promise
                uploadPromises.push(addToProjectHandler(artifactBlob, payload.projectId, artifactFileName, true));
            }
            catch (err) {
                console.error("Error converting artifact data to blob:", err);
                showToast("error", "Error", "Error converting artifact data to blob");
                // If conversion fails, we'll still try the first upload
            }
        }
        const results = await Promise.allSettled(uploadPromises);
        // console.log("RESULTSs", results);
        const allSuccessful = results.every((result) => result.status === "fulfilled" && !result.value.error);
        // If all successful, show success message and cleanup
        if (allSuccessful) {
            showToast("success", "Success", "Files uploaded successfully");
            resetAllStates();
            onHide();
            setMsgDataArtifact(null);
            if (payload.projectId) {
                // console.log("printing project id", payload.projectId);
                const userSelectedProject = allProjects.find((project) => project._id === payload.projectId);
                // console.log("printing user selected project", userSelectedProject);
                if (userSelectedProject) {
                    // converting ProjectResponse to Project
                    const projectPayload = {
                        _id: userSelectedProject._id,
                        name: userSelectedProject.name,
                        description: userSelectedProject.description,
                        file_urls: userSelectedProject.file_urls,
                        user: userSelectedProject.user,
                        created_by: userSelectedProject.created_by,
                        timestamp: userSelectedProject.timestamp,
                        parent: userSelectedProject.parent,
                        conversations: userSelectedProject.conversations,
                        files: userSelectedProject.files,
                    };
                    setSelectedProject(projectPayload);
                    setProjectIdFromURL(projectPayload._id);
                }
            }
            return true;
        }
        else {
            // Find the first error to display
            const firstError = results.find((result) => result.status === "rejected" ||
                (result.status === "fulfilled" && result.value.error));
            if (firstError) {
                let errorMessage = "Upload failed";
                if (firstError.status === "fulfilled" && firstError.value.error) {
                    errorMessage = firstError.value.error.errorMessage || errorMessage;
                }
                else if (firstError.status === "rejected") {
                    errorMessage = firstError.reason?.message || errorMessage;
                }
                console.error("Error in uploading files", firstError);
                showToast("error", "Error", errorMessage);
            }
            setIsLoading(false); // Reset loading state
            onHide(); // close the dialog
            return false;
        }
    };
    const handleConfirmUpload = async () => {
        if (!currentUploadData.blob || !currentUploadData.projectId) {
            showToast("error", "Error", "Missing required upload data");
            return;
        }
        if (!filename) {
            showToast("error", "Error", "Filename required to upload data");
            return;
        }
        setIsLoading(true);
        // commented this as now its being handled by handleBothUploads
        // const { responseData, error } = await addToProjectHandler(
        //   currentUploadData.blob,
        //   currentUploadData.projectId,
        //   filename + fileExtension, // filename + extension
        //   true
        // );
        // if (error) {
        //   console.error("Error in uploading file", error);
        //   showToast("error", "Error", error.errorMessage);
        // } else {
        //   showToast("success", "Success", "File uploaded successfully");
        //   resetAllStates();
        //   onHide();
        // }
        await handleBothUploads({ data: currentUploadData.blob, projectId: currentUploadData.projectId }, filename + fileExtension);
        setSelectedFiles([]);
        setIsLoading(false);
    };
    const handleSelectProject = () => {
        setShowDashboard(true);
    };
    useEffect(() => {
        if (!mfApi) {
            console.warn("Host MicroFrontend API not initialized");
            return;
        }
        const unsubscribers = [];
        // Subscribe to RECEIVE_PROJECT_DATA
        const receiveProjectDataUnsubscribe = mfApi.subscribe(MF_EVENTS.RECEIVE_PROJECT_DATA, async (receivedData) => {
            console.log("Received project data:", receivedData);
            setPersistedData((prevData) => {
                return prevData.map((item) => {
                    if (item.projectId === receivedData.projectId &&
                        item.storeId === receivedData.storeId) {
                        return {
                            ...item,
                            requestStatus: receivedData.requestStatus,
                            data: receivedData.data,
                        };
                    }
                    return item;
                });
            });
            setRequestLoading(false);
            if (receivedData.requestStatus === "success" && receivedData.data) {
                if (!receivedData.data)
                    throw new Error("No blob data provided");
                if (!receivedData.projectId)
                    throw new Error("No project ID provided");
                if (receivedData.bypassFileRename) {
                    if (!receivedData.fileName) {
                        setTimeout(() => {
                            setRequestLoading(false); // for update to react state to re-render
                        }, 0);
                        console.error("No filename is provided");
                        showToast("error", "Error", "No filename is provided");
                        return;
                    }
                    // Get the blob type
                    const blobType = receivedData.data.type || "application/octet-stream";
                    let finalFileExtension = "";
                    if (receivedData.fileExtension) {
                        finalFileExtension = receivedData.fileExtension.startsWith(".")
                            ? receivedData.fileExtension
                            : "." + receivedData.fileExtension;
                    }
                    else
                        finalFileExtension = getFileExtension(blobType);
                    // Function to check if a filename has a common file extension
                    // const hasValidExtension = (filename:string) => {
                    //   const lowercaseFilename = filename.toLowerCase();
                    //   return commonExtensions.some((ext) =>
                    //     lowercaseFilename.endsWith(ext)
                    //   );
                    // };
                    let completeFileName = receivedData.fileName + finalFileExtension;
                    // Only add an extension if the filename doesn't already have a valid one
                    // if (!hasValidExtension(completeFileName)) {
                    //   // Get the expected extension based on MIME type
                    //   const expectedExtension = getFileExtension(blobType);
                    //   completeFileName = receivedData.fileName + expectedExtension;
                    // }
                    // commented this as now its being handled by handleBothUploads
                    // const { responseData, error } = await addToProjectHandler(
                    //   receivedData.data,
                    //   receivedData.projectId,
                    //   completeFileName,
                    //   true
                    // );
                    // if (error) {
                    //   console.error("Error in uploading file", error);
                    //   showToast("error", "Error", error.errorMessage);
                    // } else {
                    //   showToast("success", "Success", "File uploaded successfully");
                    //   resetAllStates();
                    //   onHide();
                    // }
                    await handleBothUploads({ data: receivedData.data, projectId: receivedData.projectId }, completeFileName);
                }
                else {
                    setShowUploadDetails(true);
                    // Set initial filename
                    const blobType = receivedData.data.type || "application/octet-stream";
                    setFilename(generateUniqueFileName(blobType, receivedData?.fileName));
                    setFileExtension(getFileExtension(blobType));
                    setCurrentUploadData({
                        blob: receivedData.data,
                        projectId: receivedData.projectId,
                    });
                }
            }
            else {
                showToast("warn", "Warn", "Didn't receive data from remote");
                setRequestLoading(false);
            }
        });
        unsubscribers.push(receiveProjectDataUnsubscribe);
        return () => {
            unsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [mfApi]);
    useEffect(() => {
        if (currentUploadData.blob && currentUploadData.projectId) {
            setRequestLoading(false);
        }
    }, [currentUploadData]);
    const resetFilters = () => {
        setSearchTerm("");
        setSortOrder("date-desc");
    };
    const fetchProjects = async () => {
        try {
            setFetchingProjects(true);
            // old code for api call
            // const response = await axios.get(
            //   process.env.VITE_BASE_API_URL + "/projects"
            // );
            // const { responseData, error } = await getProjects(); // old one
            if (!user || !user.email) {
                console.warn("Dont have user details");
                showToast("warn", "Warn", "Dont have user details");
                return;
            }
            // const { responseData, error } = await getProjects(); // old one
            const { responseData, error } = await listProjectsByUser(user.email);
            if (error) {
                console.error("Error fetching projects:", error);
                showToast("error", "Error", error.errorMessage);
                return;
            }
            if (!responseData || !responseData.projects) {
                console.error("Error fetching projects in responseData:", responseData);
                showToast("error", "Error", "Error in fetching projects");
                return;
            }
            if (responseData.projects.length > 0) {
                const resProjects = responseData.projects;
                const filteredProjects = resProjects.filter((project) => !project.hasOwnProperty("parent") || project.parent === "");
                setProjects(filteredProjects);
                setAllProjects(resProjects);
            }
        }
        catch (error) {
            console.error("Error fetching projects:", error);
            showToast("error", "Error", "Failed to fetch projects");
        }
        finally {
            setFetchingProjects(false);
        }
    };
    const getSubProjectsInfo = async (projectId) => {
        setFetchingSubprojects(true);
        try {
            const { responseData, error } = await fetchSubProjects(projectId);
            if (error) {
                console.error("Error fetching sub projects info", error);
                showToast("error", "Error", "Failed to fetch subprojects");
                return [];
            }
            return responseData?.subprojects || [];
        }
        finally {
            setFetchingSubprojects(false);
        }
    };
    // First, add the reset function right after the state declarations
    const resetAllStates = () => {
        setSearchTerm("");
        setSortOrder("date-desc");
        setNavigationStack([]);
        setCurrentProject(null);
        setProjects([]);
        setSubprojectsInfo([]);
        setUserSelectedProject(null);
        setShowDashboard(false);
        setFetchingProjects(false);
        setFetchingSubprojects(false);
        setShowUploadDetails(false);
        setFilename("");
        setIsLoading(false);
        setRequestLoading(false);
        setCurrentUploadData({ blob: null, projectId: null });
    };
    // Update the useEffect to use this function
    useEffect(() => {
        if (visible) {
            resetAllStates();
            fetchProjects();
        }
    }, [visible]);
    const parseCustomTimestamp = (timestamp) => {
        if (!timestamp)
            return null;
        const [datePart, timePart] = timestamp.split(" ");
        if (!datePart || !timePart)
            return null;
        const [day, month, year] = datePart.split(":").map(Number);
        const [hours, minutes, seconds] = timePart.split(":").map(Number);
        const fullYear = year < 100 ? 2000 + year : year;
        return new Date(fullYear, month - 1, day, hours, minutes, seconds);
    };
    const filteredAndSortedProjects = useMemo(() => {
        const items = currentProject ? subprojectsInfo : projects;
        const lowerSearchTerm = searchTerm.toLowerCase();
        const filtered = items.filter((project) => project.name.toLowerCase().includes(lowerSearchTerm));
        return filtered.sort((a, b) => {
            switch (sortOrder) {
                case "name-asc":
                    return a.name.localeCompare(b.name);
                case "name-desc":
                    return b.name.localeCompare(a.name);
                case "date-asc": {
                    const dateA = parseCustomTimestamp(a.timestamp ?? "")?.getTime() || 0;
                    const dateB = parseCustomTimestamp(b.timestamp ?? "")?.getTime() || 0;
                    return dateA - dateB;
                }
                case "date-desc": {
                    const dateA = parseCustomTimestamp(a.timestamp ?? "")?.getTime() || 0;
                    const dateB = parseCustomTimestamp(b.timestamp ?? "")?.getTime() || 0;
                    return dateB - dateA;
                }
                default:
                    return 0;
            }
        });
    }, [projects, subprojectsInfo, searchTerm, sortOrder, currentProject]);
    const handleProjectNameClick = async (project) => {
        resetFilters();
        // Uncheck any selected project when navigating to subprojects
        setUserSelectedProject(null);
        const subprojects = await getSubProjectsInfo(project._id);
        setSubprojectsInfo(subprojects);
        setNavigationStack([...navigationStack, project]);
        setCurrentProject(project);
    };
    const handleGoBack = () => {
        if (navigationStack.length > 0) {
            resetFilters();
            const newStack = [...navigationStack];
            newStack.pop();
            setNavigationStack(newStack);
            const previousProject = newStack[newStack.length - 1];
            setCurrentProject(previousProject || null);
            if (previousProject) {
                getSubProjectsInfo(previousProject._id).then(setSubprojectsInfo);
            }
            else {
                setSubprojectsInfo([]);
            }
        }
    };
    const getBreadcrumbText = () => {
        if (!currentProject)
            return "";
        return navigationStack.map((project) => project.name).join(" > ");
    };
    const renderSelectedProjectInfo = () => (_jsxs("div", { className: "px-5 pb-5 h-full flex flex-col items-start justify-start min-h-[200px]", children: [_jsxs("div", { className: "w-full flex flex-col items-center gap-y-10 mb-4", children: [_jsx("h2", { className: "w-full text-base font-normal", children: "There is already a project selected:" }), _jsx("p", { className: "text-center text-xl font-bold text-[#5928ed]", children: selectedProject?.name })] }), _jsxs("div", { className: "flex flex-row-reverse gap-4 mt-auto", children: [_jsxs(Button
                    // label="Continue"
                    , { 
                        // label="Continue"
                        loading: requestLoading, className: "rounded-[6px] px-4 py-2 text-sm text-white flex items-center gap-x-2 transition-all duration-300 bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc]", onClick: handleContinue, children: [_jsx(Check, { size: 16 }), _jsx("span", { children: "Continue" })] }), _jsxs(Button
                    // label="Select another Project"
                    // className="px-6 py-3 bg-gray-100 text-gray-800 hover:bg-gray-200"
                    , { 
                        // label="Select another Project"
                        // className="px-6 py-3 bg-gray-100 text-gray-800 hover:bg-gray-200"
                        className: "rounded-[6px] px-4 py-2 text-sm text-black flex items-center gap-x-2 border border-[#d1d5db]", onClick: handleSelectProject, children: [_jsx(Folder, { size: 16, color: "#5928ed" }), _jsx("span", { children: "Select another project" })] })] })] }));
    const dialogHeader = () => {
        if (!showUploadDetails) {
            return (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs("div", { className: "flex items-center gap-4", children: [navigationStack.length > 0 && (_jsx(Button, { icon: _jsx(ChevronLeft, { size: 16 }), onClick: handleGoBack, className: "p-2 bg-white w-9", rounded: true })), _jsx("h2", { className: "text-xl font-semibold font-heading", children: "Project Selection" })] }), currentProject && (_jsx("div", { className: "text-sm text-gray-600 ml-[52px]", children: getBreadcrumbText() }))] }));
        }
        else {
            return (_jsx("div", { className: "flex flex-col gap-2", children: _jsx("div", { className: "flex items-center gap-4", children: _jsx("h2", { className: "text-xl font-semibold font-heading", children: "Update Filename" }) }) }));
        }
    };
    const renderDialogContent = () => {
        if (showUploadDetails) {
            return (_jsxs("div", { className: "px-5 pb-5 flex flex-col space-y-4", children: [_jsx("div", { className: "w-full flex justify-between items-center", children: _jsxs("label", { htmlFor: "filename", className: "block text-base font-medium text-gray-700", children: ["File Name ", _jsx("span", { className: "text-[red]", children: "*" })] }) }), _jsxs("div", { className: "flex items-center gap-x-2", children: [_jsx("input", { type: "text", id: "filename", value: filename, onChange: (e) => setFilename(e.target.value), disabled: isLoading, className: "w-full flex-1 p-2 border rounded-[8px] border-[#D1D5DB] text-sm" }), _jsx(InputText, { type: "text", readOnly: true, disabled: true, value: fileExtension, className: "w-14 p-2 border rounded-[8px] border-[#D1D5DB] text-sm" })] })] }));
        }
        if (selectedProject && !showDashboard) {
            return renderSelectedProjectInfo();
        }
        return (_jsxs("div", { className: "px-5 flex flex-col gap-y-3 h-full", children: [_jsxs("div", { className: "flex gap-3 items-center h-10 sticky top-0 z-10 bg-white", children: [_jsx(InputText, { disabled: Boolean(fetchingProjects ||
                                fetchingSubprojects ||
                                (!currentProject && projects.length < 1) ||
                                (currentProject && subprojectsInfo.length < 1)), value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), placeholder: "Search project...", className: "w-[260px] h-full border border-[#D1D5DB] px-3 py-3 rounded-[6px] text-sm", maxLength: 70 }), _jsx(Dropdown, { disabled: Boolean(fetchingProjects ||
                                fetchingSubprojects ||
                                (!currentProject && projects.length < 1) ||
                                (currentProject && subprojectsInfo.length < 1)), value: sortOrder, options: sortOptions, onChange: (e) => setSortOrder(e.value), placeholder: "Sort by", className: "custom-header-dropdown w-[200px] h-full border border-[#D1D5DB] rounded-[6px] text-sm" }), _jsx(Button, { label: "Proceed", loading: requestLoading, className: `${!userSelectedProject
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : " text-white transition-all duration-300 bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc]"} pointer-events-auto   h-10 rounded-[6px] px-4 py-2 text-sm flex items-center gap-x-2 `, disabled: !userSelectedProject, onClick: handleProceed })] }), _jsx("div", { className: "flex-1 h-[calc(100%-3.5rem)] min-h-[400px] max-h-full overflow-y-auto", children: fetchingProjects || fetchingSubprojects ? (_jsx(ProjectCardsSkeleton, {})) : filteredAndSortedProjects.length > 0 ? (_jsx("div", { className: "w-full py-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr overflow-y-auto", style: {
                            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
                        }, children: filteredAndSortedProjects.map((project) => (_jsx(ProjectCard, { isProjectSelection: true, project: project, onSelect: handleProjectSelection, onNameClick: handleProjectNameClick, isSelected: userSelectedProject?._id === project._id }, project._id))) })) : (_jsxs("div", { className: "w-full h-full flex flex-col items-center justify-center", children: [_jsx("div", { className: "w-[300px]", children: _jsx(PageNotFound, {}) }), _jsx("p", { className: "text-center text-gray-700", children: searchTerm
                                    ? `No projects found for "${searchTerm}"`
                                    : currentProject
                                        ? "No subprojects available"
                                        : "No projects available" })] })) })] }));
    };
    // Update dialog footer based on current view
    const getDialogFooter = () => {
        if (showUploadDetails) {
            return (_jsxs("div", { className: "flex justify-end p-0 gap-x-4", children: [_jsxs(Button, { className: "rounded-[6px] px-4 py-2 text-sm text-black flex items-center gap-x-2 border border-[#d1d5db]", onClick: () => {
                            setRequestLoading(false);
                            setShowUploadDetails(false);
                        }, disabled: isLoading, children: [_jsx(X, { size: 16, color: "#5928ed" }), _jsx("span", { children: "Cancel" })] }), _jsxs(Button, { className: "rounded-[6px] px-4 py-2 text-sm text-white flex items-center gap-x-2 transition-all duration-300 bg-gradient-to-r from-[#2546F0] to-[#5928ED] hover:from-[#0039cc] hover:to-[#0039cc]", onClick: handleConfirmUpload, loading: isLoading, children: [_jsx(Check, { size: 16 }), _jsx("span", { children: "Proceed" })] })] }));
        }
        return undefined;
    };
    return (_jsx(Dialog, { header: dialogHeader, visible: visible, onHide: onHide, modal: true, draggable: false, resizable: false, contentClassName: "p-0 min-w-[500px]", className: `max-w-full font-content rounded-[8px] bg-[#fff] shadow-none border-[2px]`, maskClassName: "!absolute backdrop-blur-sm !bg-transparent", footer: getDialogFooter(), closable: !isLoading, appendTo: parentRef?.current, maximizable: showDashboard && !showUploadDetails ? true : false, children: renderDialogContent() }));
};
export default ProjectSelectionDialog;
